import { APP_BASE_HREF, DatePipe, Location } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, importProvidersFrom } from '@angular/core';
import { bootstrapApplication, platformBrowser } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, UrlSerializer } from '@angular/router';
import { CreateBackupDestinationWizardService } from '@components/storage-accounts/create-backup-destination-wizard.service';
import { UbSubscribeService } from '@components/storage-accounts/ub-subscribe.service';
import { environment } from '@environments/environment';
import { MbsLoginFacade } from '@facades/login.facade';
import { PosthogModule } from '@modules/posthog/posthog.module';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import * as Sentry from '@sentry/angular';
import { AppInitService } from '@services/app-init.service';
import { DownloadService } from '@services/download.service';
import { OnlineAccessService } from '@services/onlineAccess';
import { DataChangeWatcherGuard } from '@shared/guards/data-change-watcher.guard';
import { LicensesGuard } from '@shared/guards/licenses.guard';
import { PermissionsGuard } from '@shared/guards/permissions.guard';
import { StatisticsGuard } from '@shared/guards/statistics.guard';
import { RmCommandServiceUnavailableInterceptor } from '@shared/interceptors/rm-command-error.interceptor';
import { AppStoreModule } from '@shared/store/appStore.module';
import { createAddStorageAccountWizardOpener } from '@utils/add-storage-account-wizard-opener';
import { AbilityService } from 'ability';
import { I18NEXT_SERVICE } from 'angular-i18next';
import {
  I18_NAMESPACES_MBS_UI,
  I18_NAMESPACES_MBS_UI_KIT,
  I18_NAMESPACES_MBS_UI_SHARED,
  I18_NAMESPACES_MBS_UI_WIZARDS,
  I18N_PROVIDERS
} from 'i18n';
import { supportedOpenFormats } from 'mbs-documents-viewer';
import { APP_READONLY_MODE, ModalService, PAGE_SCROLL_CONTAINER_SELECTOR, SHARED_PERSISTENT_STATE_PROVIDERS } from 'mbs-ui-kit';
import { MBS_APP_CONFIG, pageScrollContainerSelector } from 'mbs-ui-shared';
import { BuyLicenseModalComponent } from 'mbs-ui-shared@components/buy-license-modal/buy-license-modal.component';
import { BALANCE_TOKEN } from 'mbs-ui-shared@components/licenses/tokens/balance.token';
import { TicketsDemoInterceptor } from 'mbs-ui-shared@components/tickets/demo/tickets-demo.interceptor';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import { LicensesPostPaymentFacade } from 'mbs-ui-shared@facades/licenses-postpayment.facade';
import { LicensesPrepaymentFacade } from 'mbs-ui-shared@facades/licenses-prepayment.facade';
import { LicensesFacade } from 'mbs-ui-shared@facades/licenses.facade';
import { LoginFacade } from 'mbs-ui-shared@facades/login.facade';
import { CurrentUserGuard } from 'mbs-ui-shared@guards/currentUser.guard';
import { ErrorHandlerInterceptor } from 'mbs-ui-shared@interceptors/error-handler.interceptor';
import { HttpQueuedInterceptor } from 'mbs-ui-shared@interceptors/httpQueue.interceptor';
import { RedirectInterceptor } from 'mbs-ui-shared@interceptors/redirect.interceptor';
import { RequestInterceptor } from 'mbs-ui-shared@interceptors/request.interceptor';
import { SentryInterceptor } from 'mbs-ui-shared@interceptors/sentry.interceptor';
import { ServiceUnavailableInterceptor } from 'mbs-ui-shared@interceptors/service-unavailable/service-unavailable.interceptor';
import { SessionInterceptor } from 'mbs-ui-shared@interceptors/session.interceptor';
import { AppPersistentStateService } from 'mbs-ui-shared@services/app-persistent-state.service';
import { ComputersAbstractService, ComputersService } from 'mbs-ui-shared@services/computers.service';
import { ConfigurationService } from 'mbs-ui-shared@services/configuration.service';
import { ENVIRONMENT_TOKEN } from 'mbs-ui-shared@services/environments.service';
import { ProviderService } from 'mbs-ui-shared@services/provider.service';
import { RmCommandsWrapper } from 'mbs-ui-shared@services/rm-commands.wrapper';
import { RmmService } from 'mbs-ui-shared@services/rmm.service';
import { StorageAccountsService } from 'mbs-ui-shared@services/storage-accounts/storage-accounts.service';
import { ADD_STORAGE_ACCOUNT_WIZARD_OPENER } from 'mbs-ui-shared@tokens/add-storage-account-wizard.token';
import { CALCULATOR_COMPONENT_TOKEN } from 'mbs-ui-shared@tokens/calculator.token';
import { DOCUMENT_OPEN_FORMATS } from 'mbs-ui-shared@tokens/document-open-formats.token';
import { POST_PAYMENT_TOKEN } from 'mbs-ui-shared@tokens/post-payment.token';
import { SHARED_LIB_PROJECT_CONFIG, SharedLibProject } from 'mbs-ui-shared@tokens/shared-lib-project.token';
import { WEB_CONNECT_URL } from 'mbs-ui-shared@utils/connect-url';
import { ModalWrapper } from 'mbs-ui-shared@wrappers/modal.wrapper';
import { map, noop } from 'rxjs';
import { CREATE_BACKUP_DESTINATION } from 'wizards/tokens/create-backup-destination.token';
import { routes } from './app-routing.module';
import { AppComponent } from './app.component';
import { GlobalUrlSerializer } from './shared/interceptors/global-url-serializer';

// Keep router/API URLs independent from the HTML base used to locate static assets.
const ROUTER_BASE_HREF = window['__BASIC_APP_URL__'] || '/';

const suffixAP = '/AP';
const suffixAS = '/AS';

function basePath(): string {
  let path = document.querySelector('base')?.baseURI.replace(location.origin, '').replace(/\/$/, '');
  path = path.split(suffixAS)[0].split(suffixAP)[0];
  return path;
}

const demoInterceptors = [
  {
    provide: HTTP_INTERCEPTORS,
    useClass: TicketsDemoInterceptor,
    multi: true
  }
];

export function createAppInstance(config) {
  const platformRef = platformBrowser([
    {
      provide: MBS_APP_CONFIG,
      useValue: config
    }
  ]);

  return bootstrapApplication(
    AppComponent,
    {
      providers: [
        provideRouter(routes),
        provideAnimations(),
        importProvidersFrom(AppStoreModule),
        importProvidersFrom(NgbModule),
        importProvidersFrom(PosthogModule.forRoot()),
        ModalService,
        { provide: CREATE_BACKUP_DESTINATION, useExisting: CreateBackupDestinationWizardService },
        { provide: ENVIRONMENT_TOKEN, useValue: environment },
        ...SHARED_PERSISTENT_STATE_PROVIDERS,
        CurrentUserGuard,
        DataChangeWatcherGuard,
        LicensesGuard,
        PermissionsGuard,
        StatisticsGuard,
        ...(environment.production ? [] : demoInterceptors),
        { provide: APP_BASE_HREF, useValue: ROUTER_BASE_HREF },
        { provide: WEB_CONNECT_URL, useValue: '/WebConnect' },
        { provide: PAGE_SCROLL_CONTAINER_SELECTOR, useValue: pageScrollContainerSelector },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: ServiceUnavailableInterceptor,
          multi: true
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: RequestInterceptor,
          multi: true
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: SessionInterceptor,
          multi: true
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: ErrorHandlerInterceptor,
          multi: true
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: RmCommandServiceUnavailableInterceptor,
          multi: true
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: SentryInterceptor,
          multi: true
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: RedirectInterceptor,
          multi: true
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: HttpQueuedInterceptor,
          multi: true
        },
        { provide: UrlSerializer, useClass: GlobalUrlSerializer },
        ConfigurationService,
        AppInitService,
        {
          provide: APP_INITIALIZER,
          useFactory: () => noop,
          deps: [Sentry.TraceService],
          multi: true
        },
        {
          provide: APP_INITIALIZER,
          deps: [AppInitService],
          useFactory: (appInitService: AppInitService) => () => appInitService.init(),
          multi: true
        },
        {
          provide: SHARED_LIB_PROJECT_CONFIG,
          useValue: SharedLibProject.MbsUi
        },
        {
          provide: POST_PAYMENT_TOKEN,
          deps: [AuthFacade],
          useFactory: (auth: AuthFacade) => auth.currentUser$.pipe(map((r) => r?.ProviderInfo?.IsPostPayment))
        },
        {
          provide: CALCULATOR_COMPONENT_TOKEN,
          useValue: BuyLicenseModalComponent
        },
        {
          provide: DOCUMENT_OPEN_FORMATS,
          useValue: supportedOpenFormats.map(([format, label]) => ({ format, label }))
        },
        {
          provide: ADD_STORAGE_ACCOUNT_WIZARD_OPENER,
          deps: [ModalService, StorageAccountsService, I18NEXT_SERVICE],
          useFactory: createAddStorageAccountWizardOpener
        },
        {
          provide: LicensesFacade,
          deps: [AuthFacade, LicensesPostPaymentFacade, LicensesPrepaymentFacade],
          useFactory: (auth: AuthFacade, postPaymentFacade: LicensesPostPaymentFacade, prePaymentFacade: LicensesPrepaymentFacade) => {
            return auth.currentUserValue?.ProviderInfo?.IsPostPayment ? postPaymentFacade : prePaymentFacade;
          }
        },
        {
          provide: BALANCE_TOKEN,
          deps: [LicensesFacade],
          useFactory: (licensesFacade: LicensesFacade) => licensesFacade.balance
        },
        Location,
        UbSubscribeService,
        RmCommandsWrapper,
        RmmService,
        { provide: ComputersAbstractService, useClass: ComputersService },
        ComputersFacade,
        DownloadService,
        ProviderService,
        AppPersistentStateService,
        OnlineAccessService,
        ModalWrapper,
        {
          provide: APP_READONLY_MODE,
          useFactory: (ability: AbilityService): boolean => ability.can('read', 'Readonly'),
          deps: [AbilityService]
        },
        I18N_PROVIDERS(
          basePath() + environment.localePath,
          [...I18_NAMESPACES_MBS_UI, ...I18_NAMESPACES_MBS_UI_SHARED, ...I18_NAMESPACES_MBS_UI_WIZARDS, ...I18_NAMESPACES_MBS_UI_KIT],
          'shared'
        ),
        DatePipe,
        provideHttpClient(withInterceptorsFromDi()),
        {
          provide: LoginFacade,
          useClass: MbsLoginFacade
        }
      ]
    },
    {
      platformRef
    }
  );
}
